import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, ImageSourcePropType, Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View, useWindowDimensions } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, CloseIcon, Hourglass, NumberInput, Panel, Progress, Text, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import { CustomDivider } from "@/components/CustomDivider";
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { VisualFile } from "@/types/visual_file";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { SortableGridRenderItem, SortableGridDragEndCallback } from 'react-native-sortables';
import Sortable from 'react-native-sortables';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import {launchCamera, launchImageLibrary, Asset} from 'react-native-image-picker';
import { fetch } from 'expo/fetch';
import axios from 'axios';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import {
  finishUpload,
  startUpload,
  removeUpload,
  updateVisuals,
  updateUploadProgress,
  FinishUploadUpdate,
  Visual,
  UploadProgress,
  UploadProgressUpdate,
} from '@/state/uploads';
import { Buffer } from 'buffer';
import * as FileSystem from 'expo-file-system/legacy';
import { Meta } from '@/components/Meta';
import { AppText } from '@/components/AppText';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    gap: 20,
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
  },
  card: {
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  center: {
    alignItems: 'center',
    gap: 20,
    paddingTop: 20,
  },
  image: {
    width: '100%',
  },
  deleteButton: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  deleteButtonPanel: {
    padding: 10,
  },
  uploadButtons: {
    flexDirection: 'row',
    gap: 20,
  },
  progress: {
    width: '75%',
  },
});

export default function Visuals() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const {height, width} = useWindowDimensions();
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const items = useSelector((state: RootState) => state.uploads.visuals);
  const isUploading = useSelector((state: RootState) => state.uploads.isUploading);
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  useEffect(() => {
    if (communityMembership.profile.visuals.length > 0) {
      const visuals = communityMembership.profile.visuals.map<Visual>((v) => {
        return {
          id: '' + v.id,
          image: {
            uri: AppUtils.createBackendLink('/rabbl/api/visual?id=' + v.identity_file_id),
          },
          status: 'ready',
          progress: {
            uploaded: -1,
            total: -1,
          }
        }
      });
      dispatch(updateVisuals(visuals));
    }
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      showSpinner();

      if (AppUtilities.DEBUGLEVEL.uploads) {
        console.log("onSubmit:");
        console.dir(items);
      }

      const response = await AppUtilities.fetchAuthorized(
        "Visuals.onSubmit",
        "/rabbl/api/set_visuals",
        "POST",
        {
          domain: domain,
          visuals: items,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      if (edit && edit.length > 0 && router.canGoBack()) {
        router.back();
      } else {
        router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "Visuals.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain, items]);

  const pickImage = useCallback(async () => {
    try {
      // No permissions request is necessary for launching the image library.
      // Manually request permissions for videos on iOS when `allowsEditing` is set to `false`
      // and `videoExportPreset` is `'Passthrough'` (the default), ideally before launching the picker
      // so the app users aren't surprised by a system dialog after picking a video.
      // See "Invoke permissions for videos" sub section for more details.
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (!permissionResult.granted) {
        Alert.alert('Permission required', 'Permission to access the media library is required.');
        return;
      }

      // https://docs.expo.dev/versions/latest/sdk/imagepicker/#imagepickeroptions
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        quality: 1,
        exif: false,
        allowsMultipleSelection: true,
      });

      if (AppUtilities.DEBUGLEVEL.uploads) {
        AppUtilities.info(result);
      }

      if (!result.canceled) {
        result.assets.forEach((asset) => {
          const newId = AppUtils.getUUID();
          const visual : Visual = {
            id: newId,
            image: asset,
            status: 'uploading',
            progress: {
              uploaded: -1,
              total: -1,
            }
          };
          dispatch(startUpload(visual));
          upload(visual, asset, undefined);
        });
      }
    } catch (e) {
      // Might just be a cancel?
      //AppUtils.showErrorToast(AppUtilities.getErrors(e), () => pickImage(), true);
      AppUtilities.handleException(e, "Visuals.pickImage");
    }
  }, [items, communityMembership]);

  const takePicture = useCallback(async () => {
    try {
      AppUtilities.info("Visuals.takePicture");
      const permissionResult = await ImagePicker.requestCameraPermissionsAsync();

      if (!permissionResult.granted) {
        Alert.alert('Permission required', 'Permission to access the camera is required.');
        return;
      }

      const result = await launchCamera({
        mediaType: 'photo',
      });

      AppUtilities.info("Visuals.takePicture launchCamera result");
      AppUtilities.dir(result);
      if (!result.didCancel && result.assets) {
        result.assets.forEach((asset) => {
          const visual : Visual = {
            id: AppUtils.getUUID(),
            image: asset,
            status: 'uploading',
            progress: {
              uploaded: -1,
              total: -1,
            }
          };
          dispatch(startUpload(visual));
          upload(visual, undefined, asset);
        });
      }
    } catch (e) {
      // Might just be a cancel?
      //AppUtils.showErrorToast(AppUtilities.getErrors(e), () => takePicture(), true);
      AppUtilities.handleException(e, "Visuals.takePicture");
    }
  }, [items, communityMembership]);
  
  const upload = useCallback(async (visual: Visual, asset1?: ImagePicker.ImagePickerAsset, asset2?: Asset) => {
    AppUtilities.info("Visuals.upload calling AppUtils.handleVisualUpload");
    await AppUtils.handleVisualUpload(
      "VisualFile",
      communityMembership.identity_id,
      dispatch,
      visual,
      asset1,
      asset2,
    );
  }, [communityMembership]);
  
  const renderItem = useCallback<SortableGridRenderItem<Visual>>(
    ({ item }) => {
      // 60% of screen height but no bigger than 300
      const maxHeight = Math.min(
        300,
        Math.floor(height * 0.40)
      );
      return (
        <View key={'' + item.id} style={[styles.card, { height: maxHeight } ]}>
          <Image
            source={item.image}
            contentFit="contain"
            style={[
              styles.image,
              {
                height: maxHeight
              },
            ]}
            pointerEvents="none"
          />
          { !isUploading && 
            <Sortable.Touchable
              onTap={() => {
                dispatch(removeUpload(item));
              }}
              style={styles.deleteButton}
            >
              <Panel style={styles.deleteButtonPanel}>
                <CloseIcon />
              </Panel>
            </Sortable.Touchable>
          }
        </View>
      );
    },
    [items, isUploading, height]
  );

  const dragFinished = useCallback<SortableGridDragEndCallback<Visual>>(
    ({ data }) => {
      dispatch(updateVisuals(data));
    },
    [items]
  );

  let sumUploaded = 0;
  let sumTotal = 0;
  items.forEach((v) => {
    if (v.status == 'uploading') {
      if (v.progress.uploaded != -1) {
        sumUploaded += v.progress.uploaded;
      }
      if (v.progress.total != -1) {
        sumTotal += v.progress.total;
      }
    }
  });
  let progressPercent = 0;
  if (sumTotal > 0) {
    progressPercent = Math.round((sumUploaded / sumTotal) * 100);
  }
  if (AppUtilities.DEBUGLEVEL.uploads) {
    console.log("Calculated progress = " + progressPercent + " from " + sumUploaded + "," + sumTotal);
  }

  return (
    <BasicScrollView center={false} useRefreshComponent={false}>
      <Meta title={"Visuals"} />

      <View style={styles.center}>
        <AppText>Upload at least one picture. Hold and move to rearrange.</AppText>
      </View>

      <View style={styles.inputWrapper}>
        <View style={styles.uploadButtons}>
          <Button95 onPress={pickImage} disabled={isUploading}>Upload Pictures</Button95>
          {
            !AppUtilities.isWeb() && <Button95 onPress={takePicture} disabled={isUploading}>Take Picture</Button95>
          }
        </View>
      </View>

      <Sortable.Grid
        columns={AppUtils.isSmallScreen(width) ? 2 : 3}
        data={items}
        renderItem={renderItem}
        rowGap={20}
        columnGap={20}
        dragActivationDelay={100}
        showDropIndicator={true}
        onDragEnd={dragFinished}
      />

      <View style={styles.inputWrapper}>
        { isUploading && (
          <>
            <CustomText>Uploading...</CustomText>
            <Progress variant='default' percent={progressPercent} style={styles.progress} />
            <CustomDivider fullWidth={true} />
          </>
        ) }
        <Button95 onPress={onSubmit} disabled={items.length == 0 || isUploading}>{ edit ? "Save" : "Next" }</Button95>
      </View>

      <Footer />
    </BasicScrollView>
  );
}
